// 相册集 — the profile detail modals' photo album tab. Staff tick photos and
// download them with the FINDU logo watermarked in (one photo → .jpg, several
// → one .zip). The watermark is drawn in the browser on a canvas, so the
// stored originals are never touched.
//
// Two watermark looks:
//   corner — the logo lockup (diamond + serif FINDU) in the bottom-right
//            corner, ivory with the gold crown and a soft shadow: reads on
//            light and dark photos alike, like a magazine cover mark.
//   center — a large, faint (≈14%) lockup across the middle, for photos that
//            are more likely to be reposted.
//
// Props: photos (raw stored urls), resolveUrl(url) → displayable url,
//        fileBase (e.g. "1476-kiki"), isZh.
import React, { useMemo, useState } from 'react';
import { Check, Download, CheckSquare, Square } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { S } from '../staff/StaffUI';

const DIAMOND_PATHS = [
  ['M11 4H29L36 12L20 31L4 12Z', 'ink'],
  ['M4 12H36', 'ink'],
  ['M11 4L15.5 12L20 4L24.5 12L29 4', 'gold'],
  ['M15.5 12L20 31L24.5 12', 'ink'],
];
const SERIF = "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif";

// ── image loading (canvas must not be tainted) ───────────────────────────
// Same-origin /uploads load directly; other hosts go through the backend's
// /image-proxy (which only serves whitelisted photo hosts).
const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('load failed'));
  img.src = src;
});
const sameOrigin = (u) => { try { return new URL(u, window.location.href).origin === window.location.origin; } catch { return false; } };
const proxied = (u) => `${(api.defaults.baseURL || '/api').replace(/\/$/, '')}/image-proxy?url=${encodeURIComponent(u)}`;
async function loadForCanvas(url) {
  if (url.startsWith('data:') || sameOrigin(url)) return loadImage(url);
  try { return await loadImage(url); } catch { return loadImage(proxied(url)); }
}

// ── watermark ─────────────────────────────────────────────────────────────
function drawLockup(ctx, x, y, h, { ink, gold, shadow }) {
  // Diamond (40×35 viewBox) then the wordmark, baseline-aligned to its middle.
  const dScale = h / 35;
  const dW = 40 * dScale;
  ctx.save();
  if (shadow) { ctx.shadowColor = 'rgba(0,0,0,.38)'; ctx.shadowBlur = h * 0.45; ctx.shadowOffsetY = h * 0.04; }
  ctx.translate(x, y);
  ctx.save();
  ctx.scale(dScale, dScale);
  ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  ctx.lineWidth = 1.5;
  DIAMOND_PATHS.forEach(([d, tone]) => { ctx.strokeStyle = tone === 'gold' ? gold : ink; ctx.stroke(new Path2D(d)); });
  ctx.restore();
  const fontPx = h * 0.92;
  ctx.font = `500 ${fontPx}px ${SERIF}`;
  ctx.fillStyle = ink;
  ctx.textBaseline = 'middle';
  const text = 'FINDU';
  const spacing = fontPx * 0.16;
  let tx = dW + h * 0.42;
  for (const ch of text) { ctx.fillText(ch, tx, h * 0.5); tx += ctx.measureText(ch).width + spacing; }
  ctx.restore();
  return tx - spacing; // total width
}
function lockupWidth(ctx, h) {
  const fontPx = h * 0.92;
  ctx.font = `500 ${fontPx}px ${SERIF}`;
  const spacing = fontPx * 0.16;
  let w = 40 * (h / 35) + h * 0.42;
  for (const ch of 'FINDU') w += ctx.measureText(ch).width + spacing;
  return w - spacing;
}
export async function watermarkToBlob(img, style = 'corner') {
  const W = img.naturalWidth, H = img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);
  const short = Math.min(W, H);
  if (style === 'center') {
    const h = Math.max(28, W * 0.075);
    const w = lockupWidth(ctx, h);
    ctx.globalAlpha = 0.14;
    drawLockup(ctx, (W - w) / 2, (H - h) / 2, h, { ink: '#FFFDF9', gold: '#FFFDF9', shadow: false });
    ctx.globalAlpha = 1;
  } else {
    const h = Math.max(18, short * 0.045);
    const w = lockupWidth(ctx, h);
    const m = short * 0.045;
    ctx.globalAlpha = 0.92;
    drawLockup(ctx, W - w - m, H - h - m, h, { ink: '#FFFDF9', gold: '#D9C49A', shadow: true });
    ctx.globalAlpha = 1;
  }
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.92));
}

// ── tiny STORE-only zip (no dependency) ──────────────────────────────────
const CRC_TABLE = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
const crc32 = (u8) => { let c = 0xFFFFFFFF; for (let i = 0; i < u8.length; i++) c = CRC_TABLE[(c ^ u8[i]) & 0xFF] ^ (c >>> 8); return (c ^ 0xFFFFFFFF) >>> 0; };
async function makeZip(files) { // files: [{ name, blob }]
  const enc = new TextEncoder();
  const parts = []; const central = []; let offset = 0;
  for (const f of files) {
    const data = new Uint8Array(await f.blob.arrayBuffer());
    const name = enc.encode(f.name);
    const crc = crc32(data);
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0x0800, true); // UTF-8 names
    lh.setUint16(8, 0, true); lh.setUint32(14, crc, true); lh.setUint32(18, data.length, true); lh.setUint32(22, data.length, true);
    lh.setUint16(26, name.length, true);
    parts.push(lh.buffer, name, data);
    const ch = new DataView(new ArrayBuffer(46));
    ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true); ch.setUint16(8, 0x0800, true);
    ch.setUint32(16, crc, true); ch.setUint32(20, data.length, true); ch.setUint32(24, data.length, true);
    ch.setUint16(28, name.length, true); ch.setUint32(42, offset, true);
    central.push(ch.buffer, name);
    offset += 30 + name.length + data.length;
  }
  const cSize = central.reduce((s, p) => s + (p.byteLength ?? p.length), 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, cSize, true); end.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end.buffer], { type: 'application/zip' });
}
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// CSS preview of the watermark over a thumbnail (matches the canvas output).
function PreviewMark({ style }) {
  const center = style === 'center';
  return (
    <span aria-hidden="true" style={{
      position: 'absolute', display: 'inline-flex', alignItems: 'center', gap: 5, pointerEvents: 'none',
      ...(center ? { left: '50%', top: '50%', transform: 'translate(-50%,-50%)', opacity: 0.4 } : { right: 7, bottom: 7, opacity: 0.95, filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.45))' }),
    }}>
      <svg width={center ? 22 : 12} height={center ? 19 : 10.5} viewBox="0 0 40 35" fill="none" strokeLinejoin="round" strokeLinecap="round">
        {DIAMOND_PATHS.map(([d, tone]) => <path key={d} d={d} stroke={tone === 'gold' && !center ? '#D9C49A' : '#FFFDF9'} strokeWidth="1.5" />)}
      </svg>
      <span style={{ fontFamily: SERIF, color: '#FFFDF9', fontSize: center ? 20 : 11, letterSpacing: '.16em', lineHeight: 1 }}>FINDU</span>
    </span>
  );
}

export default function PhotoAlbumTab({ photos = [], resolveUrl, fileBase = 'findu', isZh = true }) {
  const t = (zh, en) => (isZh ? zh : en);
  const list = useMemo(() => photos.map((p) => (resolveUrl ? resolveUrl(p) : p)).filter(Boolean), [photos, resolveUrl]);
  const [picked, setPicked] = useState(() => new Set());
  const [style, setStyle] = useState('corner');
  const [busy, setBusy] = useState(null); // { done, total }

  const toggle = (i) => setPicked((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const allOn = list.length > 0 && picked.size === list.length;
  const safeBase = String(fileBase || 'findu').replace(/[\\/:*?"<>|\s]+/g, '-').slice(0, 60);

  const download = async () => {
    const idx = [...picked].sort((a, b) => a - b);
    if (!idx.length || busy) return;
    try { if (document.fonts?.load) await document.fonts.load(`500 48px ${SERIF}`); } catch { /* fallback serif */ }
    setBusy({ done: 0, total: idx.length });
    const files = []; let failed = 0;
    for (let k = 0; k < idx.length; k++) {
      try {
        const img = await loadForCanvas(list[idx[k]]);
        const blob = await watermarkToBlob(img, style);
        files.push({ name: `${safeBase}-${String(idx[k] + 1).padStart(2, '0')}.jpg`, blob });
      } catch { failed += 1; }
      setBusy({ done: k + 1, total: idx.length });
    }
    try {
      if (files.length === 1) saveBlob(files[0].blob, files[0].name);
      else if (files.length > 1) saveBlob(await makeZip(files), `${safeBase}-相册.zip`);
      if (failed) toast.warn(t(`${failed} 张照片无法处理（来源不允许跨域读取），已跳过`, `${failed} photo(s) could not be processed and were skipped`));
      else if (files.length) toast.success(t(`已下载 ${files.length} 张带水印照片`, `Downloaded ${files.length} watermarked photo(s)`));
    } finally { setBusy(null); }
  };

  if (!list.length) {
    return <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: S.sub }}>{t('暂无照片', 'No photos yet')}</div>;
  }

  const seg = (on) => ({ height: 36, padding: '0 14px', border: 'none', background: on ? S.ink : 'transparent', color: on ? '#FFFDF9' : S.ink, fontFamily: 'inherit', fontSize: 12.5, letterSpacing: '.12em', cursor: 'pointer' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setPicked(allOn ? new Set() : new Set(list.map((_, i) => i)))}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 12px', border: `1px solid ${S.ink}`, background: 'transparent', color: S.ink, fontFamily: 'inherit', fontSize: 12.5, letterSpacing: '.1em', cursor: 'pointer' }}>
            {allOn ? <CheckSquare size={14} strokeWidth={1.5} /> : <Square size={14} strokeWidth={1.5} />}{allOn ? t('取消全选', 'Clear') : t('全选', 'Select all')}
          </button>
          <span style={{ fontSize: 12.5, color: S.sub }}>{t(`已选 ${picked.size} / ${list.length} 张`, `${picked.size} / ${list.length} selected`)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, letterSpacing: '.2em', color: S.sub }}>{t('水印', 'WATERMARK')}</span>
          <div role="group" aria-label={t('水印样式', 'Watermark style')} style={{ display: 'inline-flex', border: `1px solid ${S.ink}` }}>
            <button type="button" aria-pressed={style === 'corner'} onClick={() => setStyle('corner')} style={seg(style === 'corner')}>{t('角落标', 'Corner')}</button>
            <button type="button" aria-pressed={style === 'center'} onClick={() => setStyle('center')} style={seg(style === 'center')}>{t('居中淡印', 'Center')}</button>
          </div>
          <button type="button" onClick={download} disabled={!picked.size || !!busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', border: 'none', background: S.ink, color: '#FFFDF9', fontFamily: 'inherit', fontSize: 12.5, letterSpacing: '.18em', cursor: picked.size && !busy ? 'pointer' : 'default', opacity: picked.size && !busy ? 1 : 0.4 }}>
            <Download size={14} strokeWidth={1.5} aria-hidden="true" />
            {busy ? t(`处理中 ${busy.done}/${busy.total}`, `Working ${busy.done}/${busy.total}`) : t(picked.size > 1 ? `下载 ${picked.size} 张（zip）` : '下载照片', picked.size > 1 ? `Download ${picked.size} (zip)` : 'Download')}
          </button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
        {list.map((src, i) => {
          const on = picked.has(i);
          return (
            <button key={src + i} type="button" onClick={() => toggle(i)} aria-pressed={on} aria-label={t(`照片 ${i + 1}`, `Photo ${i + 1}`)}
              style={{ position: 'relative', padding: 0, border: on ? `2px solid ${S.ink}` : '1px solid rgba(17,17,17,.12)', background: '#EFEAE2', aspectRatio: '3 / 4', overflow: 'hidden', cursor: 'pointer' }}>
              <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <PreviewMark style={style} />
              <span style={{ position: 'absolute', top: 7, left: 7, width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: on ? S.ink : 'rgba(255,253,249,.85)', border: `1px solid ${on ? S.ink : 'rgba(17,17,17,.25)'}`, color: '#FFFDF9' }}>
                {on && <Check size={14} strokeWidth={2} aria-hidden="true" />}
              </span>
              <span style={{ position: 'absolute', top: 8, right: 9, fontFamily: SERIF, fontSize: 13, color: '#FFFDF9', textShadow: '0 1px 3px rgba(0,0,0,.5)' }}>{String(i + 1).padStart(2, '0')}</span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 12, color: S.sub, lineHeight: 1.7 }}>
        {t('点照片勾选。下载的照片会印上 FINDU 标志，原图不受影响。一张直接下载 JPG，多张打包成 zip。', 'Tap to select. Downloads carry the FINDU mark; the stored originals are untouched. One photo → JPG, several → zip.')}
      </div>
    </div>
  );
}
